import React, { Component } from 'react';
import moment from 'moment';
import Switch from 'react-switch';
import Modal from 'react-bootstrap/Modal';
import { isEmpty } from 'lodash';

import { getDegMinSec, getUTM, getWGS } from '../../utils/locationUtil';
import {
  getObservationTypeMeta,
  shouldShowResultTag,
  normalizeTagLabel
} from '../../utils/observationTypeUtil';
import PhotoItem from '../../components/views/observation/PhotoItem';
import GraphItem from '../../components/views/observation/GraphItem';
import FileItem from '../../components/views/observation/FileItem';
import PDFItem from '../../components/views/observation/PDFItem';
import DraggableModalDialog from './DraggableModalDialog';

import _ from 'lodash';

import { generateObservationPDF } from '../../utils/observationPdfGenerator';
import { getPayloadAgentName } from '../../utils/agentUtil';

import '../views/observation/Observation.scss';
import './Modal.scss';

const capitalize = s => (s ? s[0].toUpperCase() + s.slice(1) : '');
const extractChipLabel = value => {
  if (!value) return '';
  if (typeof value === 'string') return value.trim();
  if (typeof value === 'object') {
    const candidate = [value.label, value.name, value.value].find(
      item => item !== undefined && item !== null && String(item).trim()
    );
    return candidate ? String(candidate).trim() : '';
  }
  return String(value).trim();
};
const getChipLabels = data => {
  const labels = [];
  const seen = new Set();
  const pushUniqueLabel = value => {
    const label = extractChipLabel(value);
    if (!label || !shouldShowResultTag(label)) return;
    const dedupeKey =
      normalizeTagLabel(label) ||
      label
        .toString()
        .trim()
        .toLowerCase();
    if (!dedupeKey || seen.has(dedupeKey)) return;
    seen.add(dedupeKey);
    labels.push(label);
  };
  const concepts = Array.isArray(data && data.concepts)
    ? data.concepts
    : data && data.concepts
    ? [data.concepts]
    : [];
  concepts.forEach(concept => {
    pushUniqueLabel(concept);
  });
  const tags = Array.isArray(data && data.tags)
    ? data.tags
    : data && data.tags
    ? [data.tags]
    : [];
  tags.forEach(tag => {
    pushUniqueLabel(tag);
  });
  return labels;
};

const VALUE_CREATED = 'created';
const VALUE_UPDATED = 'updated';
const VALUE_DELETED = 'deleted';
const VALUE_UNCHANGED = 'unchanged';
const IMAGE_MEDIA_TYPES = new Set([
  'jpeg',
  'jpg',
  'png',
  'gif',
  'webp',
  'bmp',
  'tiff',
  'heic',
  'heif'
]);

class ObservationModal extends Component {
  state = {
    genericDetailsOpen: false,
    attachmentsOpen: false,
    specificDetailsOpen: true,
    historyOpen: false,
    technicalDetailsOpen: false,
    rawDataOpen: false,
    UTM: false,
    copying: {},
    doneCopying: {},
    pdfGenerating: false
  };

  handleSwitch = value => {
    this.setState({ UTM: value });
  };

  handlePrint = async () => {
    if (this.state.pdfGenerating) return;
    this.setState({ pdfGenerating: true });
    try {
      const { data } = this.props;
      const attributes = Array.isArray(data.attributes) ? data.attributes : [];

      const specificDetailsList = attributes
        .filter(item => this.checkIfSpecificDetail(item))
        .map(item => ({
          label: this.getLabel(item.key),
          value: this.formatSpecificDetailValue(item.value)
        }));

      const editHistoryEntries = this.getEditHistoryEntries(data);
      const originalEntryMeta = this.getOriginalEntryMeta(data);

      await generateObservationPDF(
        data,
        specificDetailsList,
        editHistoryEntries,
        originalEntryMeta,
        this.props.currentUser || ''
      );
    } finally {
      this.setState({ pdfGenerating: false });
    }
  };

  getDifference = prevVersions => {
    const localVersions = [...prevVersions];
    var result = {};
    for (var i = 0; i < this.props.data.attributes.length; i++) {
      result[this.props.data.attributes[i].key] = this.props.data.attributes[
        i
      ].value;
    }
    delete result.prevVersions;

    let changes = [];
    let prevVersion = {};
    localVersions.reverse();
    localVersions.forEach((version, index) => {
      if (index === 0) {
        let change = this.differenceMapper(version, result);
        if (change != null) changes.push(change);
        prevVersion = version;
      } else {
        let change = this.differenceMapper(version, prevVersion);
        if (change != null) changes.push(change);
        prevVersion = version;
      }
    });

    return changes;
  };

  differenceMapper = (obj1, obj2) => {
    if (this.isFunction(obj1) || this.isFunction(obj2)) {
      throw 'Invalid argument. Function given, object expected.';
    }
    if (this.isValue(obj1) || this.isValue(obj2)) {
      let change = this.compareValues(obj1, obj2);
      if (
        change === VALUE_CREATED ||
        change === VALUE_DELETED ||
        change === VALUE_UPDATED
      ) {
        return {
          type: this.compareValues(obj1, obj2),
          prev: obj1 === undefined ? '' : obj1,
          next: obj2 === undefined ? '' : obj2
        };
      } else {
        return null;
      }
    }

    var diff = {};
    for (var key in obj1) {
      if (this.isFunction(obj1[key])) {
        continue;
      }

      var value2 = undefined;
      if (obj2[key] !== undefined) {
        value2 = obj2[key];
      }

      let change = null;
      if (key === 'tags' && this.isArray(obj1[key]) && this.isArray(value2)) {
        change = this.differenceTagsMapper(obj1[key], value2);
      } else {
        change = this.differenceMapper(obj1[key], value2);
      }

      if (
        change !== null &&
        change !== undefined &&
        Object.keys(change).length > 0
      )
        diff[key] = change;
    }

    for (var key2 in obj2) {
      if (this.isFunction(obj2[key2])) {
        continue;
      }

      var value1 = undefined;
      if (obj1[key2] !== undefined) {
        value1 = obj1[key2];
      }

      let change = null;
      if (key === 'tags' && this.isArray(obj2[key2]) && this.isArray(value1)) {
        change = this.differenceTagsMapper(value1, obj2[key2]);
      } else {
        change = this.differenceMapper(value1, obj2[key2]);
      }

      if (
        change !== null &&
        change !== undefined &&
        Object.keys(change).length > 0
      )
        diff[key2] = change;
    }

    delete diff.prevVersions;
    delete diff.recievedAt;
    delete diff.images;
    delete diff.editorId;
    delete diff.editorName;
    delete diff.user;
    delete diff.lastUpdateAt;
    delete diff.userid;
    delete diff.status;
    delete diff.trackId;
    delete diff.editor;

    if (Object.prototype.hasOwnProperty.call(obj2, 'lastUpdateAt'))
      diff.timestamp = obj2.lastUpdateAt;
    if (Object.prototype.hasOwnProperty.call(obj2, 'user'))
      diff.user = obj1.user;

    if (Object.prototype.hasOwnProperty.call(obj2, 'editor')) {
      diff.editor = obj2.editor;
    } else if (Object.prototype.hasOwnProperty.call(obj2, 'editorName')) {
      diff.editor = obj2.editorName;
    }

    return diff;
  };

  differenceTagsMapper = (obj1, obj2) => {
    if (!_.isEqual(obj1.sort(), obj2.sort())) {
      return {
        type: VALUE_UPDATED,
        prev: obj1 === undefined ? '' : obj1.join(', '),
        next: obj2 === undefined ? '' : obj2.join(', ')
      };
    }
  };

  isFunction = x => {
    return Object.prototype.toString.call(x) === '[object Function]';
  };

  isArray = x => {
    return Object.prototype.toString.call(x) === '[object Array]';
  };

  isDate = x => {
    return Object.prototype.toString.call(x) === '[object Date]';
  };

  isObject = x => {
    return Object.prototype.toString.call(x) === '[object Object]';
  };

  isValue = x => {
    return !this.isObject(x) && !this.isArray(x);
  };

  compareValues = (value1, value2) => {
    if (value1 === value2) {
      return VALUE_UNCHANGED;
    }
    if (
      this.isDate(value1) &&
      this.isDate(value2) &&
      value1.getTime() === value2.getTime()
    ) {
      return VALUE_UNCHANGED;
    }
    if (value1 === undefined) {
      return VALUE_CREATED;
    }
    if (value2 === undefined) {
      return VALUE_DELETED;
    }
    return VALUE_UPDATED;
  };

  hasMediaImage = media => {
    return media
      ? media.findIndex(
          element =>
            element &&
            element.Media &&
            (String(element.Media.mediaType || '')
              .toLowerCase()
              .startsWith('image/') ||
              IMAGE_MEDIA_TYPES.has(
                String(element.Media.mediaType || '').toLowerCase()
              ))
        )
      : -1;
  };

  hasMediaPDF = media => {
    return media
      ? media.findIndex(element => element.Media.mediaType === 'pdf')
      : -1;
  };

  copyTextToClipboard = async (text, stateKey = text) => {
    await this.setState({
      copying: {
        ...this.state.copying,
        [stateKey]: true
      }
    });
    navigator.clipboard.writeText(text);

    await new Promise(resolve => setTimeout(resolve, 300));

    await this.setState({
      copying: {
        ...this.state.copying,
        [stateKey]: false
      },
      doneCopying: {
        ...this.state.doneCopying,
        [stateKey]: true
      }
    });

    await new Promise(resolve => setTimeout(resolve, 500));

    await this.setState({
      doneCopying: {
        ...this.state.doneCopying,
        [stateKey]: false
      }
    });
  };

  stringifyRawData = value => {
    const seen = new WeakSet();
    try {
      return JSON.stringify(
        value,
        (key, item) => {
          if (typeof item === 'object' && item !== null) {
            if (seen.has(item)) return '[Circular]';
            seen.add(item);
          }
          return item;
        },
        2
      );
    } catch (error) {
      return '{}';
    }
  };

  getLabel = key => {
    const label = this.props.labels.find(label => {
      return label.zxx.replace(/^[^.]*\./, '') === key;
    });
    const found = label?.[this.props.selectedLanguage];
    if (found) return found;
    // No prefLabel found – convert camelCase/PascalCase to human-readable
    return key
      .replace(/([A-Z]+)([A-Z][a-z])/g, '$1 $2')
      .replace(/([a-z\d])([A-Z])/g, '$1 $2')
      .replace(/^./, s => s.toUpperCase())
      .trim();
  };

  checkIfSpecificDetail = item => {
    const normalizedKey = String(item.key || '').toLowerCase();
    return (
      item.key !== 'location' &&
      item.key !== 'actions' &&
      item.key !== 'userid' &&
      item.key !== 'user' &&
      item.key !== 'images' &&
      item.key !== 'tags' &&
      item.key !== 'appVersion' &&
      item.key !== 'observation_type' &&
      item.key !== 'pid' &&
      item.key !== 'timestamp' &&
      item.key !== 'type' &&
      item.key !== 'description' &&
      item.key !== 'geometry' &&
      item.key !== 'id' &&
      item.key !== 'status' &&
      item.key !== 'track_id' &&
      item.key !== 'trackId' &&
      normalizedKey !== 'trackid' &&
      item.key !== 'recievedAt' &&
      item.key !== 'editorId' &&
      item.key !== 'editorName' &&
      item.key !== 'lastUpdateAt' &&
      item.key !== 'projectname' &&
      item.key !== 'projectName' &&
      item.key !== 'version' &&
      item.key !== 'prevVersions' &&
      ((typeof item.value === 'string' && item.value !== '') ||
        (Array.isArray(item.value) && item.value.length > 0))
    );
  };

  checkIfJSON = item => {
    return item.value instanceof Array || item.value instanceof Object;
  };

  formatSpecificDetailValue = value => {
    if (value === null || value === undefined) return '-';

    // Keep array values readable in one line while preserving all items.
    if (Array.isArray(value)) {
      const listValues = value
        .map(item => this.formatSpecificDetailValue(item))
        .filter(item => item && item !== '-');
      return listValues.length > 0 ? listValues.join(', ') : '-';
    }

    // Flatten object payloads to their field values for GUI display.
    if (this.isObject(value)) {
      const objectValues = Object.values(value)
        .map(item => this.formatSpecificDetailValue(item))
        .filter(item => item && item !== '-');
      if (objectValues.length > 0) {
        return objectValues.join(', ');
      }
    }

    const normalized = String(value)
      .replace(/[_]+/g, ' ')
      .replace(/[[\]{}"]/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();

    if (!normalized) return '-';

    return normalized.charAt(0).toUpperCase() + normalized.slice(1);
  };

  getHistorySourceSnapshots = data => {
    const attributes = Array.isArray(data && data.attributes)
      ? data.attributes
      : [];
    const prevVersionsAttr = attributes.find(
      attr => attr && attr.key === 'prevVersions'
    );
    const prevVersions = Array.isArray(
      prevVersionsAttr && prevVersionsAttr.value
    )
      ? prevVersionsAttr.value
      : [];
    return prevVersions.filter(item => this.isObject(item));
  };

  getCurrentHistorySnapshot = data => {
    const attributes = Array.isArray(data && data.attributes)
      ? data.attributes
      : [];
    const snapshot = {};
    attributes.forEach(attribute => {
      if (!attribute || !attribute.key) return;
      if (attribute.key === 'prevVersions') return;
      snapshot[attribute.key] = attribute.value;
    });
    snapshot.lastUpdateAt =
      snapshot.lastUpdateAt ||
      snapshot.timestamp ||
      (data && (data.lastUpdateAt || data.when));
    snapshot.editor =
      snapshot.editor || snapshot.editorName || getPayloadAgentName(data);
    return snapshot;
  };

  getHistoryTimestamp = snapshot => {
    const rawValue =
      (snapshot &&
        (snapshot.lastUpdateAt || snapshot.timestamp || snapshot.when)) ||
      '';
    const parsed = Date.parse(rawValue);
    return Number.isFinite(parsed) ? parsed : 0;
  };

  normalizeHistorySnapshot = snapshot => {
    if (!this.isObject(snapshot)) return {};
    const normalized = { ...snapshot };
    const nestedValues = this.isObject(snapshot.values)
      ? snapshot.values
      : null;
    // Some payloads keep observation attributes under `values`. Flatten those
    // keys so history diffs compare the actual domain fields consistently.
    if (nestedValues) {
      Object.keys(nestedValues).forEach(key => {
        normalized[key] = nestedValues[key];
      });
    }
    return normalized;
  };

  // Normalize history text for readable UI labels/values.
  formatHistoryDisplayText = value => {
    const sanitized = String(value || '')
      .replace(/[_]+/g, ' ')
      .replace(/[{}"]/g, ' ')
      .replace(/\[/g, ' ')
      .replace(/\]/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
    if (!sanitized) return '-';
    return sanitized.charAt(0).toUpperCase() + sanitized.slice(1);
  };

  formatHistoryAttribute = key => {
    const label = this.getLabel(key);
    return this.formatHistoryDisplayText(label || key);
  };

  formatHistoryValue = value => {
    if (value === null || value === undefined) return '-';
    if (Array.isArray(value)) {
      if (!value.length) return '-';
      return value.map(item => this.formatHistoryValue(item)).join(', ');
    }
    if (this.isObject(value)) {
      const entries = Object.keys(value).map(key => {
        const formattedKey = this.formatHistoryDisplayText(key);
        const formattedValue = this.formatHistoryValue(value[key]);
        return `${formattedKey}: ${formattedValue}`;
      });
      return entries.length ? entries.join(', ') : '-';
    }
    return this.formatHistoryDisplayText(value);
  };

  normalizeHistoryComparableString = value =>
    String(value || '')
      .trim()
      .toLowerCase()
      .replace(/[_\s]+/g, '');

  normalizeHistoryComparableValue = value => {
    if (value === null || value === undefined) return value;
    if (typeof value === 'string') {
      return this.normalizeHistoryComparableString(value);
    }
    if (Array.isArray(value)) {
      return value.map(item => this.normalizeHistoryComparableValue(item));
    }
    if (this.isObject(value)) {
      return Object.keys(value)
        .sort()
        .reduce((result, key) => {
          result[key] = this.normalizeHistoryComparableValue(value[key]);
          return result;
        }, {});
    }
    return value;
  };

  areHistoryValuesEqual = (key, left, right) => {
    const normalizedLeft = this.normalizeHistoryComparableValue(left);
    const normalizedRight = this.normalizeHistoryComparableValue(right);
    return _.isEqual(normalizedLeft, normalizedRight);
  };

  isHistoryEmptyValue = value => {
    if (value === null || value === undefined) return true;
    if (typeof value === 'string') return value.trim() === '';
    if (Array.isArray(value)) return value.length === 0;
    return false;
  };

  isSparseHistorySnapshot = snapshot => {
    if (!this.isObject(snapshot)) return true;
    const metaKeys = new Set([
      'prevVersions',
      'location',
      'actions',
      'userid',
      'user',
      'images',
      'tags',
      'appVersion',
      'observation_type',
      'pid',
      'timestamp',
      'type',
      'geometry',
      'id',
      'status',
      'trackId',
      'track_id',
      'trackid',
      'recievedAt',
      'version',
      'values',
      'editor',
      'editorName',
      'editorId',
      'lastUpdateAt',
      'projectname',
      'projectName'
    ]);
    const valueKeys = Object.keys(snapshot).filter(key => !metaKeys.has(key));
    if (valueKeys.length <= 3) return true;
    const nonEmptyValueCount = valueKeys.filter(
      key => !this.isHistoryEmptyValue(snapshot[key])
    ).length;
    return nonEmptyValueCount <= 3;
  };

  getHistoryChanges = (newerSnapshot, olderSnapshot) => {
    const ignoredKeys = new Set([
      'prevVersions',
      'location',
      'actions',
      'userid',
      'user',
      'images',
      'tags',
      'appVersion',
      'observation_type',
      'pid',
      'timestamp',
      'type',
      'geometry',
      'id',
      'status',
      'trackId',
      'track_id',
      'trackid',
      'recievedAt',
      'version',
      'values',
      'editor',
      'editorName',
      'editorId',
      'lastUpdateAt',
      'projectname',
      'projectName'
    ]);

    const keys = new Set([
      ...Object.keys(newerSnapshot || {}),
      ...Object.keys(olderSnapshot || {})
    ]);

    const changes = [];
    Array.from(keys).forEach(key => {
      if (ignoredKeys.has(key)) return;
      const hasOldKey = Boolean(
        olderSnapshot &&
          Object.prototype.hasOwnProperty.call(olderSnapshot, key)
      );
      const hasNewKey = Boolean(
        newerSnapshot &&
          Object.prototype.hasOwnProperty.call(newerSnapshot, key)
      );
      const newValue =
        hasNewKey && newerSnapshot ? newerSnapshot[key] : undefined;
      const oldValue =
        hasOldKey && olderSnapshot ? olderSnapshot[key] : undefined;
      // Skip rows where both sides are effectively empty.
      if (
        this.isHistoryEmptyValue(oldValue) &&
        this.isHistoryEmptyValue(newValue)
      ) {
        return;
      }
      // Keep key-additions and empty-to-value transitions visible in history.
      if (this.areHistoryValuesEqual(key, newValue, oldValue)) return;
      changes.push({
        attribute: this.formatHistoryAttribute(key),
        oldValue: this.formatHistoryValue(oldValue),
        newValue: this.formatHistoryValue(newValue)
      });
    });
    return changes;
  };

  getEditHistoryEntries = data => {
    const olderSnapshots = this.getHistorySourceSnapshots(data);
    if (!olderSnapshots.length) return [];

    const currentSnapshot = this.normalizeHistorySnapshot(
      this.getCurrentHistorySnapshot(data)
    );
    const snapshots = [currentSnapshot, ...olderSnapshots]
      .filter(snapshot => this.isObject(snapshot))
      .map(snapshot => this.normalizeHistorySnapshot(snapshot))
      .sort(
        (left, right) =>
          this.getHistoryTimestamp(right) - this.getHistoryTimestamp(left)
      );

    const edits = [];
    for (let index = 0; index < snapshots.length - 1; index += 1) {
      const newer = snapshots[index];
      const older = snapshots[index + 1];
      const changes = this.getHistoryChanges(newer, older);
      if (!changes.length) continue;
      edits.push({
        when: newer.lastUpdateAt || newer.timestamp || newer.when || '',
        agent: newer.editor || newer.editorName || newer.user || '',
        editorId: newer.editorId || newer.userid || '',
        changes
      });
    }

    return edits;
  };

  getOriginalEntryMeta = data => {
    const currentSnapshot = this.normalizeHistorySnapshot(
      this.getCurrentHistorySnapshot(data)
    );
    const olderSnapshots = this.getHistorySourceSnapshots(data).map(snapshot =>
      this.normalizeHistorySnapshot(snapshot)
    );
    const snapshots = [currentSnapshot, ...olderSnapshots].filter(snapshot =>
      this.isObject(snapshot)
    );
    if (!snapshots.length) {
      return { when: '', agent: '' };
    }

    const sortedAscending = snapshots.sort(
      (left, right) =>
        this.getHistoryTimestamp(left) - this.getHistoryTimestamp(right)
    );
    const oldest = sortedAscending[0] || {};
    const when =
      oldest.when ||
      oldest.timestamp ||
      oldest.lastUpdateAt ||
      (data && data.when) ||
      '';
    const agent = String(
      getPayloadAgentName(oldest) ||
        oldest.user ||
        oldest.editor ||
        oldest.editorName ||
        getPayloadAgentName(data) ||
        ''
    ).trim();

    return { when, agent };
  };

  render() {
    const { ...props } = this.props;
    const attributes = Array.isArray(props.data.attributes)
      ? props.data.attributes
      : [];
    const chipLabels = getChipLabels(props.data);
    const showTagsInSpecificDetails = false;
    const observationTypeMeta = getObservationTypeMeta(
      props.data.observationType
    );
    const allMedia = Array.isArray(props.data.media) ? props.data.media : [];
    const imageMedia = allMedia.filter(
      mediaItem =>
        mediaItem &&
        mediaItem.Media &&
        (String(mediaItem.Media.mediaType || '')
          .toLowerCase()
          .startsWith('image/') ||
          IMAGE_MEDIA_TYPES.has(
            String(mediaItem.Media.mediaType || '').toLowerCase()
          ))
    );
    const firstImage = imageMedia.length > 0 ? imageMedia[0] : null;
    const hasAnyAttachments =
      allMedia.length > 0 ||
      (Array.isArray(props.data.files) && props.data.files.length > 0);
    const displayAgentName = getPayloadAgentName(props.data);
    const isHydrating = Boolean(props.data && props.data.isHydrating);
    const hydrationError = Boolean(props.data && props.data.hydrationError);
    const editHistoryEntries = this.getEditHistoryEntries(props.data);
    const originalEntryMeta = this.getOriginalEntryMeta(props.data);
    const hasHistoryPayload = Boolean(
      props.data.attributes &&
        props.data.attributes.length > 0 &&
        props.data.attributes.findIndex(
          attr => attr.key === 'prevVersions' && !isEmpty(attr.value)
        ) >= 0
    );
    const hasEditHistory = hasHistoryPayload && editHistoryEntries.length > 0;
    const trackIdAttribute = attributes.find(attr => {
      const key = String((attr && attr.key) || '').toLowerCase();
      return (
        (key === 'trackid' || key === 'track_id') &&
        !isEmpty(attr && attr.value)
      );
    });
    const trackIdValue = props.data.trackId || (trackIdAttribute || {}).value;
    const rawDataText = this.stringifyRawData(props.data || {});
    const rawDataCopyKey = '__observation_raw_data__';
    return (
      <Modal
        dialogAs={DraggableModalDialog}
        show={props.open}
        onHide={() => {
          this.setState({ specificDetailsOpen: true });
          props.close();
        }}
        centered={true}
        size="md"
      >
        <Modal.Header closeButton>
          <h5
            className="modal-title"
            id="exampleModalLabel"
            style={{ flex: 1 }}
          >
            {props.data.observationClass ? (
              <span>{capitalize(props.data.observationClass)}</span>
            ) : (
              <span></span>
            )}
          </h5>
          <button
            type="button"
            className="btn btn-sm btn-outline-secondary observation-print-btn"
            onClick={this.handlePrint}
            disabled={this.state.pdfGenerating}
            title="Export as PDF"
            style={{ marginRight: '8px' }}
          >
            {this.state.pdfGenerating ? (
              <span
                className="spinner-border spinner-border-sm"
                role="status"
                aria-hidden="true"
                style={{ width: '14px', height: '14px', borderWidth: '2px' }}
              />
            ) : (
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="15"
                height="15"
                fill="currentColor"
                viewBox="0 0 16 16"
              >
                <path d="M2.5 8a.5.5 0 1 0 0-1 .5.5 0 0 0 0 1z" />
                <path d="M5 1a2 2 0 0 0-2 2v2H2a2 2 0 0 0-2 2v3a2 2 0 0 0 2 2h1v1a2 2 0 0 0 2 2h6a2 2 0 0 0 2-2v-1h1a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2h-1V3a2 2 0 0 0-2-2H5zM4 3a1 1 0 0 1 1-1h6a1 1 0 0 1 1 1v2H4V3zm1 5a2 2 0 0 0-2 2v1H2a1 1 0 0 1-1-1V7a1 1 0 0 1 1-1h12a1 1 0 0 1 1 1v3a1 1 0 0 1-1 1h-1v-1a2 2 0 0 0-2-2H5zm7 2v3a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1v-3a1 1 0 0 1 1-1h6a1 1 0 0 1 1 1z" />
              </svg>
            )}
          </button>
        </Modal.Header>
        <Modal.Body
          className={`pb-0${isHydrating ? ' observation-modal-hydrating' : ''}`}
          onMouseDown={event => event.stopPropagation()}
        >
          {(isHydrating || hydrationError) && (
            <div
              className={`observation-modal-hydration-status${
                hydrationError ? ' is-error' : ''
              }`}
              role="status"
              aria-live="polite"
            >
              {hydrationError && (
                <span className="observation-modal-hydration-label">
                  Showing cached content. Could not refresh details.
                </span>
              )}
              {!hydrationError && (
                <span
                  className="observation-modal-hydration-progress"
                  aria-hidden="true"
                />
              )}
            </div>
          )}
          <div className="row no-gutters">
            <div className="col-auto  pl-2 pr-3 pt-3 pb-3 ml-1 photoItem">
              {this.hasMediaImage(props.data.media) > -1 && (
                <img
                  src={firstImage.url}
                  className="img-fluid rounded"
                  alt=""
                  style={{ width: '7.5rem', height: '7.5rem' }}
                  onClick={e => {
                    e.stopPropagation();
                    props.openImage({
                      images: imageMedia,
                      index: 0
                    });
                  }}
                />
              )}
              {this.hasMediaImage(props.data.media) === -1 &&
                !hasAnyAttachments && (
                  <div className="modal-no-attachment-holder rounded">
                    <span
                      className="modal-no-attachment-camera"
                      aria-hidden="true"
                    >
                      <svg
                        width="18"
                        height="18"
                        viewBox="0 0 24 24"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M4.5 7.5H8L9.8 5.5H14.2L16 7.5H19.5C20.6 7.5 21.5 8.4 21.5 9.5V17.5C21.5 18.6 20.6 19.5 19.5 19.5H4.5C3.4 19.5 2.5 18.6 2.5 17.5V9.5C2.5 8.4 3.4 7.5 4.5 7.5Z"
                          stroke="currentColor"
                          strokeWidth="1.8"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                        <circle
                          cx="12"
                          cy="13.2"
                          r="3.2"
                          stroke="currentColor"
                          strokeWidth="1.8"
                        />
                      </svg>
                      <span className="modal-no-attachment-camera-slash" />
                    </span>
                  </div>
                )}
              {this.hasMediaImage(props.data.media) === -1 &&
                hasAnyAttachments && (
                  <img
                    src="./assets/img/photos/no-image-placeholder.jpg"
                    className="img-fluid rounded"
                    alt=""
                    style={{ width: '7.5rem', height: '7.5rem' }}
                  />
                )}
            </div>
            <div className="col">
              <div className="card-block pl-0 pr-3 py-2 pt-3">
                <h6 className="card-title mb-1 mt-1 font-weight-bold pointer">
                  {observationTypeMeta.icon && (
                    <img
                      src={observationTypeMeta.icon}
                      className="observation-type"
                      alt={observationTypeMeta.label}
                    />
                  )}
                  {observationTypeMeta.label}
                </h6>
                <p className="card-text text-muted mb-1 font-weight-light">
                  {moment(props.data.when).format('DD MMM YYYY - HH:mm')}{' '}
                  {displayAgentName ? (
                    <span> - {capitalize(displayAgentName)}</span>
                  ) : (
                    <span></span>
                  )}
                </p>
                <p className="card-text text-muted mb-2 font-weight-light">
                  {props.data.description || 'No description'}
                  {this.state.copying[props.data.description] && (
                    <div className="spinner-border copy-spinner" role="status">
                      <span className="sr-only">Loading...</span>
                    </div>
                  )}
                  {this.state.doneCopying[props.data.description] && (
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="16"
                      height="16"
                      fill="currentColor"
                      className="bi bi-check-lg check-icon"
                      viewBox="0 0 16 16"
                    >
                      <path d="M12.736 3.97a.733.733 0 0 1 1.047 0c.286.289.29.756.01 1.05L7.88 12.01a.733.733 0 0 1-1.065.02L3.217 8.384a.757.757 0 0 1 0-1.06.733.733 0 0 1 1.047 0l3.052 3.093 5.4-6.425z" />
                    </svg>
                  )}
                  {!this.state.copying[props.data.description] &&
                    !this.state.doneCopying[props.data.description] && (
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="16"
                        height="16"
                        fill="currentColor"
                        className="bi bi-copy copy-icon"
                        viewBox="0 0 16 16"
                        onClick={() =>
                          this.copyTextToClipboard(props.data.description)
                        }
                      >
                        <path
                          fillRule="evenodd"
                          d="M4 2a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2zm2-1a1 1 0 0 0-1 1v8a1 1 0 0 0 1 1h8a1 1 0 0 0 1-1V2a1 1 0 0 0-1-1zM2 5a1 1 0 0 0-1 1v8a1 1 0 0 0 1 1h8a1 1 0 0 0 1-1v-1h1v1a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h1v1z"
                        />
                      </svg>
                    )}
                </p>
                {chipLabels.map((label, key) => (
                  <span
                    key={key}
                    className="badge badge-secondary p-2 mr-1 mb-1 text-uppercase"
                  >
                    {label}
                  </span>
                ))}
              </div>
            </div>
          </div>

          <div className="card rounded-0 border-bottom-0 card-full-width">
            <div
              className="card-header"
              id="headingTwo"
              onClick={() =>
                this.setState({
                  genericDetailsOpen: !this.state.genericDetailsOpen
                })
              }
            >
              <h6 className="mb-0" style={{ float: 'left' }}>
                Generic details
              </h6>
              <div
                className={
                  this.state.genericDetailsOpen
                    ? `super-treeview-triangle-btn-up`
                    : `super-treeview-triangle-btn-down`
                }
              />
            </div>

            {this.state.genericDetailsOpen && (
              <div>
                <div className="list-group px-3">
                  {props.data.group && props.data.group.groupName && (
                    <div className="detail-container no-gutters">
                      <label
                        htmlFor=""
                        className="detail-container__title col-form-label text-muted"
                      >
                        Group name
                      </label>
                      <label
                        htmlFor=""
                        className="col-form-label font-weight-light pl-4 detail-container__value"
                      >
                        {props.data.group.groupName}
                        {this.state.copying[props.data.group.groupName] && (
                          <div
                            className="spinner-border copy-spinner"
                            role="status"
                          >
                            <span className="sr-only">Loading...</span>
                          </div>
                        )}
                        {this.state.doneCopying[props.data.group.groupName] && (
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="16"
                            height="16"
                            fill="currentColor"
                            className="bi bi-check-lg check-icon"
                            viewBox="0 0 16 16"
                          >
                            <path d="M12.736 3.97a.733.733 0 0 1 1.047 0c.286.289.29.756.01 1.05L7.88 12.01a.733.733 0 0 1-1.065.02L3.217 8.384a.757.757 0 0 1 0-1.06.733.733 0 0 1 1.047 0l3.052 3.093 5.4-6.425z" />
                          </svg>
                        )}
                        {!this.state.copying[props.data.group.groupName] &&
                          !this.state.doneCopying[
                            props.data.group.groupName
                          ] && (
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width="16"
                              height="16"
                              fill="currentColor"
                              className="bi bi-copy copy-icon"
                              viewBox="0 0 16 16"
                              onClick={() =>
                                this.copyTextToClipboard(
                                  props.data.group.groupName
                                )
                              }
                            >
                              <path
                                fillRule="evenodd"
                                d="M4 2a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2zm2-1a1 1 0 0 0-1 1v8a1 1 0 0 0 1 1h8a1 1 0 0 0 1-1V2a1 1 0 0 0-1-1zM2 5a1 1 0 0 0-1 1v8a1 1 0 0 0 1 1h8a1 1 0 0 0 1-1v-1h1v1a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h1v1z"
                              />
                            </svg>
                          )}
                      </label>
                    </div>
                  )}
                  {displayAgentName && (
                    <div className="detail-container no-gutters">
                      <label
                        htmlFor=""
                        className="detail-container__title col-form-label text-muted"
                      >
                        Agent name
                      </label>
                      <label
                        htmlFor=""
                        className="col-form-label font-weight-light pl-4 detail-container__value"
                      >
                        {capitalize(displayAgentName)}
                        {this.state.copying[displayAgentName] && (
                          <div
                            className="spinner-border copy-spinner"
                            role="status"
                          >
                            <span className="sr-only">Loading...</span>
                          </div>
                        )}
                        {this.state.doneCopying[displayAgentName] && (
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="16"
                            height="16"
                            fill="currentColor"
                            className="bi bi-check-lg check-icon"
                            viewBox="0 0 16 16"
                          >
                            <path d="M12.736 3.97a.733.733 0 0 1 1.047 0c.286.289.29.756.01 1.05L7.88 12.01a.733.733 0 0 1-1.065.02L3.217 8.384a.757.757 0 0 1 0-1.06.733.733 0 0 1 1.047 0l3.052 3.093 5.4-6.425z" />
                          </svg>
                        )}
                        {!this.state.copying[displayAgentName] &&
                          !this.state.doneCopying[displayAgentName] && (
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width="16"
                              height="16"
                              fill="currentColor"
                              className="bi bi-copy copy-icon"
                              viewBox="0 0 16 16"
                              onClick={() =>
                                this.copyTextToClipboard(displayAgentName)
                              }
                            >
                              <path
                                fillRule="evenodd"
                                d="M4 2a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2zm2-1a1 1 0 0 0-1 1v8a1 1 0 0 0 1 1h8a1 1 0 0 0 1-1V2a1 1 0 0 0-1-1zM2 5a1 1 0 0 0-1 1v8a1 1 0 0 0 1 1h8a1 1 0 0 0 1-1v-1h1v1a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h1v1z"
                              />
                            </svg>
                          )}
                      </label>
                    </div>
                  )}
                  {props.data.where && (
                    <div className="detail-container no-gutters">
                      <label
                        htmlFor=""
                        className="detail-container__title col-form-label text-muted"
                      >
                        Degrees
                      </label>
                      <label
                        htmlFor=""
                        className="col-form-label font-weight-light pl-4 detail-container__value"
                      >
                        {getDegMinSec(
                          props.data.where.coordinates[1],
                          props.data.where.coordinates[0]
                        )}
                        {this.state.copying[
                          getDegMinSec(
                            props.data.where.coordinates[1],
                            props.data.where.coordinates[0]
                          )
                        ] && (
                          <div
                            className="spinner-border copy-spinner"
                            role="status"
                          >
                            <span className="sr-only">Loading...</span>
                          </div>
                        )}
                        {this.state.doneCopying[
                          getDegMinSec(
                            props.data.where.coordinates[1],
                            props.data.where.coordinates[0]
                          )
                        ] && (
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="16"
                            height="16"
                            fill="currentColor"
                            className="bi bi-check-lg check-icon"
                            viewBox="0 0 16 16"
                          >
                            <path d="M12.736 3.97a.733.733 0 0 1 1.047 0c.286.289.29.756.01 1.05L7.88 12.01a.733.733 0 0 1-1.065.02L3.217 8.384a.757.757 0 0 1 0-1.06.733.733 0 0 1 1.047 0l3.052 3.093 5.4-6.425z" />
                          </svg>
                        )}
                        {!this.state.copying[
                          getDegMinSec(
                            props.data.where.coordinates[1],
                            props.data.where.coordinates[0]
                          )
                        ] &&
                          !this.state.doneCopying[
                            getDegMinSec(
                              props.data.where.coordinates[1],
                              props.data.where.coordinates[0]
                            )
                          ] && (
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width="16"
                              height="16"
                              fill="currentColor"
                              className="bi bi-copy copy-icon"
                              viewBox="0 0 16 16"
                              onClick={() =>
                                this.copyTextToClipboard(
                                  getDegMinSec(
                                    props.data.where.coordinates[1],
                                    props.data.where.coordinates[0]
                                  )
                                )
                              }
                            >
                              <path
                                fillRule="evenodd"
                                d="M4 2a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2zm2-1a1 1 0 0 0-1 1v8a1 1 0 0 0 1 1h8a1 1 0 0 0 1-1V2a1 1 0 0 0-1-1zM2 5a1 1 0 0 0-1 1v8a1 1 0 0 0 1 1h8a1 1 0 0 0 1-1v-1h1v1a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h1v1z"
                              />
                            </svg>
                          )}
                      </label>
                    </div>
                  )}
                  {props.data.where && (
                    <div className="detail-container no-gutters">
                      <label
                        htmlFor=""
                        className="detail-container__title col-form-label text-muted"
                      >
                        Coordinates
                      </label>
                      {!this.state.UTM && (
                        <label
                          htmlFor=""
                          className="col-form-label font-weight-light pl-4 detail-container__value"
                        >
                          {getWGS(props.data.where.coordinates)}
                          {this.state.copying[
                            getWGS(props.data.where.coordinates)
                          ] && (
                            <div
                              className="spinner-border copy-spinner"
                              role="status"
                            >
                              <span className="sr-only">Loading...</span>
                            </div>
                          )}
                          {this.state.doneCopying[
                            getWGS(props.data.where.coordinates)
                          ] && (
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width="16"
                              height="16"
                              fill="currentColor"
                              className="bi bi-check-lg check-icon"
                              viewBox="0 0 16 16"
                            >
                              <path d="M12.736 3.97a.733.733 0 0 1 1.047 0c.286.289.29.756.01 1.05L7.88 12.01a.733.733 0 0 1-1.065.02L3.217 8.384a.757.757 0 0 1 0-1.06.733.733 0 0 1 1.047 0l3.052 3.093 5.4-6.425z" />
                            </svg>
                          )}
                          {!this.state.copying[
                            getWGS(props.data.where.coordinates)
                          ] &&
                            !this.state.doneCopying[
                              getWGS(props.data.where.coordinates)
                            ] && (
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="16"
                                height="16"
                                fill="currentColor"
                                className="bi bi-copy copy-icon"
                                viewBox="0 0 16 16"
                                onClick={() =>
                                  this.copyTextToClipboard(
                                    getWGS(props.data.where.coordinates)
                                  )
                                }
                              >
                                <path
                                  fillRule="evenodd"
                                  d="M4 2a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2zm2-1a1 1 0 0 0-1 1v8a1 1 0 0 0 1 1h8a1 1 0 0 0 1-1V2a1 1 0 0 0-1-1zM2 5a1 1 0 0 0-1 1v8a1 1 0 0 0 1 1h8a1 1 0 0 0 1-1v-1h1v1a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h1v1z"
                                />
                              </svg>
                            )}
                        </label>
                      )}
                      {this.state.UTM && (
                        <label
                          htmlFor=""
                          className="col-form-label font-weight-light pl-4 detail-container__value"
                        >
                          {getUTM(
                            props.data.where.coordinates[1],
                            props.data.where.coordinates[0]
                          )}
                          {this.state.copying[
                            getUTM(
                              props.data.where.coordinates[1],
                              props.data.where.coordinates[0]
                            )
                          ] && (
                            <div
                              className="spinner-border copy-spinner"
                              role="status"
                            >
                              <span className="sr-only">Loading...</span>
                            </div>
                          )}
                          {this.state.doneCopying[
                            getUTM(
                              props.data.where.coordinates[1],
                              props.data.where.coordinates[0]
                            )
                          ] && (
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width="16"
                              height="16"
                              fill="currentColor"
                              className="bi bi-check-lg check-icon"
                              viewBox="0 0 16 16"
                            >
                              <path d="M12.736 3.97a.733.733 0 0 1 1.047 0c.286.289.29.756.01 1.05L7.88 12.01a.733.733 0 0 1-1.065.02L3.217 8.384a.757.757 0 0 1 0-1.06.733.733 0 0 1 1.047 0l3.052 3.093 5.4-6.425z" />
                            </svg>
                          )}
                          {!this.state.copying[
                            getUTM(
                              props.data.where.coordinates[1],
                              props.data.where.coordinates[0]
                            )
                          ] &&
                            !this.state.doneCopying[
                              getUTM(
                                props.data.where.coordinates[1],
                                props.data.where.coordinates[0]
                              )
                            ] && (
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="16"
                                height="16"
                                fill="currentColor"
                                className="bi bi-copy copy-icon"
                                viewBox="0 0 16 16"
                                onClick={() =>
                                  this.copyTextToClipboard(
                                    getUTM(
                                      props.data.where.coordinates[1],
                                      props.data.where.coordinates[0]
                                    )
                                  )
                                }
                              >
                                <path
                                  fillRule="evenodd"
                                  d="M4 2a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2zm2-1a1 1 0 0 0-1 1v8a1 1 0 0 0 1 1h8a1 1 0 0 0 1-1V2a1 1 0 0 0-1-1zM2 5a1 1 0 0 0-1 1v8a1 1 0 0 0 1 1h8a1 1 0 0 0 1-1v-1h1v1a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h1v1z"
                                />
                              </svg>
                            )}
                        </label>
                      )}
                    </div>
                  )}
                  {props.data.where && (
                    <div className="detail-container no-gutters">
                      <label
                        htmlFor=""
                        className="detail-container__title col-form-label text-muted"
                      >
                        Coordinate type
                      </label>
                      <div>
                        <label htmlFor="" className="col-form-label text-muted">
                          WGS84
                        </label>
                        <label
                          htmlFor=""
                          className="col-form-label font-weight-light pl-4 detail-container__value mx-2 p-0 mt-1"
                        >
                          <Switch
                            height={21}
                            width={42}
                            onChange={() =>
                              this.setState({ UTM: !this.state.UTM })
                            }
                            checked={this.state.UTM}
                            uncheckedIcon={false}
                            checkedIcon={false}
                          />{' '}
                        </label>

                        <label htmlFor="" className="col-form-label text-muted">
                          UTM
                        </label>
                      </div>
                    </div>
                  )}
                  {props.data.attributes &&
                    props.data.attributes.findIndex(
                      attr =>
                        attr.key === 'lastUpdateAt' && !isEmpty(attr.value)
                    ) >= 0 && (
                      <div className="detail-container no-gutters mb-1">
                        <label
                          htmlFor=""
                          className="detail-container__title col-form-label text-muted"
                        >
                          Last updated at
                        </label>
                        <label
                          htmlFor=""
                          className="col-form-label font-weight-light pl-4 detail-container__value"
                        >
                          {moment(
                            props.data.attributes.find(
                              attr => attr.key === 'lastUpdateAt'
                            ).value
                          ).format('DD MMM YYYY - HH:mm')}
                        </label>
                      </div>
                    )}
                </div>

                <div
                  id="collapseTwo"
                  className="collapse show"
                  aria-labelledby="headingTwo"
                  data-parent="#accordion"
                ></div>
              </div>
            )}
          </div>

          <div className="card rounded-0 border-bottom-0 card-full-width">
            <div
              className="card-header"
              id="headingTwo"
              onClick={() =>
                this.setState({
                  specificDetailsOpen: !this.state.specificDetailsOpen
                })
              }
            >
              <h6 className="mb-0" style={{ float: 'left' }}>
                Specific details
              </h6>
              <div
                className={
                  this.state.specificDetailsOpen
                    ? `super-treeview-triangle-btn-up`
                    : `super-treeview-triangle-btn-down`
                }
              />
            </div>

            {this.state.specificDetailsOpen && (
              <div>
                {attributes && attributes.length > 0 && (
                  <div className="list-group px-3">
                    <div>
                      {showTagsInSpecificDetails &&
                        props.data.tags &&
                        props.data.tags.length > 0 && (
                          <div className="detail-container no-gutters">
                            <label
                              htmlFor=""
                              className="detail-container__title col-form-label text-muted"
                            >
                              Tags
                            </label>
                            <label
                              htmlFor=""
                              className="col-form-label font-weight-light pl-4 detail-container__value"
                            >
                              {Array.isArray(props.data.tags)
                                ? props.data.tags.join(', ')
                                : props.data.tags}

                              {this.state.copying[
                                Array.isArray(props.data.tags)
                                  ? props.data.tags.join(', ')
                                  : props.data.tags
                              ] && (
                                <div
                                  className="spinner-border copy-spinner"
                                  role="status"
                                >
                                  <span className="sr-only">Loading...</span>
                                </div>
                              )}
                              {this.state.doneCopying[
                                Array.isArray(props.data.tags)
                                  ? props.data.tags.join(', ')
                                  : props.data.tags
                              ] && (
                                <svg
                                  xmlns="http://www.w3.org/2000/svg"
                                  width="16"
                                  height="16"
                                  fill="currentColor"
                                  className="bi bi-check-lg check-icon"
                                  viewBox="0 0 16 16"
                                >
                                  <path d="M12.736 3.97a.733.733 0 0 1 1.047 0c.286.289.29.756.01 1.05L7.88 12.01a.733.733 0 0 1-1.065.02L3.217 8.384a.757.757 0 0 1 0-1.06.733.733 0 0 1 1.047 0l3.052 3.093 5.4-6.425z" />
                                </svg>
                              )}
                              {!this.state.copying[
                                Array.isArray(props.data.tags)
                                  ? props.data.tags.join(', ')
                                  : props.data.tags
                              ] &&
                                !this.state.doneCopying[
                                  Array.isArray(props.data.tags)
                                    ? props.data.tags.join(', ')
                                    : props.data.tags
                                ] && (
                                  <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    width="16"
                                    height="16"
                                    fill="currentColor"
                                    className="bi bi-copy copy-icon"
                                    viewBox="0 0 16 16"
                                    onClick={() =>
                                      this.copyTextToClipboard(
                                        Array.isArray(props.data.tags)
                                          ? props.data.tags.join(', ')
                                          : props.data.tags
                                      )
                                    }
                                  >
                                    <path
                                      fillRule="evenodd"
                                      d="M4 2a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2zm2-1a1 1 0 0 0-1 1v8a1 1 0 0 0 1 1h8a1 1 0 0 0 1-1V2a1 1 0 0 0-1-1zM2 5a1 1 0 0 0-1 1v8a1 1 0 0 0 1 1h8a1 1 0 0 0 1-1v-1h1v1a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h1v1z"
                                    />
                                  </svg>
                                )}
                            </label>
                          </div>
                        )}

                      {props.data.attributes &&
                        props.data.attributes.length > 0 &&
                        props.data.attributes
                          .filter((item, _) => this.checkIfSpecificDetail(item))
                          .map((item, _) => (
                            <div className="detail-container no-gutters">
                              <label
                                htmlFor=""
                                className="detail-container__title col-form-label text-muted"
                              >
                                {this.getLabel(item.key)}
                              </label>
                              <label
                                htmlFor=""
                                className="col-form-label font-weight-light pl-4 detail-container__value"
                              >
                                {this.formatSpecificDetailValue(
                                  item && item.value
                                )}
                                {this.state.copying[item.value] && (
                                  <div
                                    className="spinner-border copy-spinner"
                                    role="status"
                                  >
                                    <span className="sr-only">Loading...</span>
                                  </div>
                                )}
                                {this.state.doneCopying[item.value] && (
                                  <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    width="16"
                                    height="16"
                                    fill="currentColor"
                                    className="bi bi-check-lg check-icon"
                                    viewBox="0 0 16 16"
                                  >
                                    <path d="M12.736 3.97a.733.733 0 0 1 1.047 0c.286.289.29.756.01 1.05L7.88 12.01a.733.733 0 0 1-1.065.02L3.217 8.384a.757.757 0 0 1 0-1.06.733.733 0 0 1 1.047 0l3.052 3.093 5.4-6.425z" />
                                  </svg>
                                )}
                                {!this.state.copying[item.value] &&
                                  !this.state.doneCopying[item.value] && (
                                    <svg
                                      xmlns="http://www.w3.org/2000/svg"
                                      width="16"
                                      height="16"
                                      fill="currentColor"
                                      className="bi bi-copy copy-icon"
                                      viewBox="0 0 16 16"
                                      onClick={() =>
                                        this.copyTextToClipboard(item.value)
                                      }
                                    >
                                      <path
                                        fillRule="evenodd"
                                        d="M4 2a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2zm2-1a1 1 0 0 0-1 1v8a1 1 0 0 0 1 1h8a1 1 0 0 0 1-1V2a1 1 0 0 0-1-1zM2 5a1 1 0 0 0-1 1v8a1 1 0 0 0 1 1h8a1 1 0 0 0 1-1v-1h1v1a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h1v1z"
                                      />
                                    </svg>
                                  )}
                              </label>
                            </div>
                          ))}
                    </div>

                    {props.data.group && props.data.group.groupId.length === 4 && (
                      <div>
                        {props.data.attributes.map((item, key) => (
                          <div>
                            {!isEmpty(item.value) &&
                              item.key !== 'prevVersions' && (
                                <div className="detail-container no-gutters">
                                  <label
                                    htmlFor=""
                                    className="detail-container__title col-form-label text-muted"
                                  >
                                    {item.key}
                                  </label>
                                  <label
                                    htmlFor=""
                                    className="col-form-label font-weight-light pl-4 detail-container__value"
                                  >
                                    {typeof item.value === 'object' && (
                                      <div>
                                        {Object.keys(item.value).map(
                                          (itemNest, keyNest) => (
                                            <div>
                                              {typeof item.value[itemNest] ===
                                                'object' && (
                                                <div>
                                                  {Object.keys(
                                                    item.value[itemNest]
                                                  ).map((deepNest, deepKey) => (
                                                    <div>
                                                      {
                                                        item.value[itemNest][
                                                          deepNest
                                                        ]
                                                      }
                                                    </div>
                                                  ))}
                                                </div>
                                              )}
                                              {Array.isArray(
                                                item.value[itemNest]
                                              ) && (
                                                <div>
                                                  {item.value[itemNest].map(
                                                    (deepNest, deepKey) => (
                                                      <div>
                                                        {deepNest.value}
                                                      </div>
                                                    )
                                                  )}
                                                </div>
                                              )}
                                              {typeof item.value[itemNest] !==
                                                'object' &&
                                                !Array.isArray(
                                                  item.value[itemNest]
                                                ) &&
                                                item.value[itemNest]}
                                            </div>
                                          )
                                        )}
                                      </div>
                                    )}
                                    {Array.isArray(item.value) && (
                                      <div>
                                        {item.value.map((itemNest, keyNest) => (
                                          <div>{itemNest.value}</div>
                                        ))}
                                      </div>
                                    )}
                                    {typeof item.value !== 'object' &&
                                      !Array.isArray(item.value) &&
                                      item.value}
                                  </label>
                                </div>
                              )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>

          {(this.hasMediaImage(props.data.media) > -1 ||
            this.hasMediaPDF(props.data.media) > -1 ||
            (props.data.graph && props.data.graph.length > 0) ||
            (props.data.files && props.data.files.length > 0)) && (
            <div className="card rounded-0 border-bottom-0 card-full-width">
              <div
                className="card-header"
                id="headingTwo"
                onClick={() =>
                  this.setState({
                    attachmentsOpen: !this.state.attachmentsOpen
                  })
                }
              >
                <h6 className="mb-0" style={{ float: 'left' }}>
                  Attachments
                </h6>
                <div
                  className={
                    this.state.attachmentsOpen
                      ? `super-treeview-triangle-btn-up`
                      : `super-treeview-triangle-btn-down`
                  }
                />
              </div>

              {this.state.attachmentsOpen && (
                <div className="mb-4">
                  {this.hasMediaImage(props.data.media) > -1 && (
                    <div className="list-group px-3 mt-4">
                      <div className="detail-container no-gutters">
                        <label
                          htmlFor=""
                          className="detail-container__title col-form-label"
                        >
                          <h6>Photos</h6>
                        </label>
                        <label
                          htmlFor=""
                          className="col-form-label font-weight-light pl-4 detail-container__value"
                        >
                          <span className="badge badge-primary badge-pill">
                            {imageMedia.length}
                          </span>
                        </label>
                      </div>
                    </div>
                  )}

                  {this.hasMediaImage(props.data.media) > -1 && (
                    <div className="container">
                      <div className="row" aria-labelledby="headingOne">
                        {props.data.media
                          .filter(
                            mediaItem =>
                              mediaItem &&
                              mediaItem.Media &&
                              (String(mediaItem.Media.mediaType || '')
                                .toLowerCase()
                                .startsWith('image/') ||
                                IMAGE_MEDIA_TYPES.has(
                                  String(
                                    mediaItem.Media.mediaType || ''
                                  ).toLowerCase()
                                ))
                          )
                          .map((item, key) => (
                            <div key={key} className="col-4">
                              <PhotoItem
                                image={item}
                                onOpen={() =>
                                  props.openImage({
                                    images: imageMedia,
                                    index: key
                                  })
                                }
                              />
                            </div>
                          ))}
                      </div>
                    </div>
                  )}

                  {this.hasMediaPDF(props.data.media) > -1 && (
                    <div className="list-group px-3 mt-4">
                      <div className="detail-container no-gutters">
                        <label
                          htmlFor=""
                          className="detail-container__title col-form-label"
                        >
                          <h6>Documents</h6>
                        </label>
                        <label
                          htmlFor=""
                          className="col-form-label font-weight-light pl-4 detail-container__value"
                        >
                          <span className="badge badge-primary badge-pill">
                            {props.data.media.length}
                          </span>
                        </label>
                      </div>
                    </div>
                  )}

                  {this.hasMediaPDF(props.data.media) > -1 && (
                    <div className="container">
                      {props.data.media.map((item, key) => {
                        return item.Media.mediaType === 'pdf' ? (
                          <div>
                            <PDFItem key={key} item={item} />
                          </div>
                        ) : (
                          <div />
                        );
                      })}
                    </div>
                  )}

                  {props.data.graph && props.data.graph.length > 0 && (
                    <div className="list-group px-3 mt-4">
                      <div className="detail-container no-gutters">
                        <label
                          htmlFor=""
                          className="detail-container__title col-form-label"
                        >
                          <h6>Photos</h6>
                        </label>
                      </div>
                    </div>
                  )}

                  {props.data.graph && props.data.graph.length > 0 && (
                    <div className="container">
                      <div className="row" aria-labelledby="headingOne">
                        <GraphItem graph={props.data.graph.graph} />
                      </div>
                    </div>
                  )}

                  {props.data.files && props.data.files.length > 0 && (
                    <div className="list-group px-3 mt-4">
                      <div className="detail-container no-gutters">
                        <label
                          htmlFor=""
                          className="detail-container__title col-form-label"
                        >
                          <h6>Files</h6>
                        </label>
                        <label
                          htmlFor=""
                          className="col-form-label font-weight-light pl-4 detail-container__value"
                        >
                          <span className="badge badge-primary badge-pill">
                            {props.data.files.length}
                          </span>
                        </label>
                      </div>
                    </div>
                  )}

                  {props.data.files && props.data.files.length > 0 && (
                    <div className="container">
                      <div className="row" aria-labelledby="headingOne">
                        {props.data.files.map((item, key) => (
                          <div className="col-4">
                            <FileItem key={key} file={item} />
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {hasEditHistory && (
            <div className="card rounded-0 border-bottom-0 card-full-width">
              <div
                className="card-header"
                id="headingTwo"
                onClick={() =>
                  this.setState({ historyOpen: !this.state.historyOpen })
                }
              >
                <h6 className="mb-0" style={{ float: 'left' }}>
                  History
                </h6>
                <div
                  className={
                    this.state.historyOpen
                      ? `super-treeview-triangle-btn-up`
                      : `super-treeview-triangle-btn-down`
                  }
                />
              </div>

              {this.state.historyOpen && (
                <div className="edit-history-list px-3 py-2">
                  {editHistoryEntries.map((entry, entryIndex) => (
                    <div className="edit-history-entry" key={entryIndex}>
                      <div className="edit-history-header">
                        <span className="edit-history-label">Edited on </span>
                        <span className="edit-history-meta">
                          {entry.when && moment(entry.when).isValid()
                            ? moment(entry.when).format('DD MMM YYYY - HH:mm')
                            : entry.when || '-'}
                          {' by '}
                          {entry.agent || '-'}
                        </span>
                      </div>
                      <table className="edit-history-table">
                        <thead>
                          <tr>
                            <th>Attribute</th>
                            <th>Old</th>
                            <th>New</th>
                          </tr>
                        </thead>
                        <tbody>
                          {entry.changes.map((change, changeIndex) => (
                            <tr key={changeIndex}>
                              <td className="edit-history-attribute">
                                {change.attribute}
                              </td>
                              <td className="edit-history-values">
                                {change.oldValue}
                              </td>
                              <td className="edit-history-values">
                                {change.newValue}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ))}
                  <div className="edit-history-original">
                    Original data entry date:{' '}
                    {originalEntryMeta.when &&
                    moment(originalEntryMeta.when).isValid()
                      ? moment(originalEntryMeta.when).format(
                          'DD MMM YYYY - HH:mm'
                        )
                      : originalEntryMeta.when || '-'}
                    {originalEntryMeta.agent
                      ? `, ${originalEntryMeta.agent}`
                      : ''}
                  </div>
                </div>
              )}
            </div>
          )}

          <div className="card rounded-0 border-bottom-0 card-full-width">
            <div
              className="card-header"
              id="headingTwo"
              onClick={() =>
                this.setState({
                  technicalDetailsOpen: !this.state.technicalDetailsOpen
                })
              }
            >
              <h6 className="mb-0" style={{ float: 'left' }}>
                Technical details
              </h6>
              <div
                className={
                  this.state.technicalDetailsOpen
                    ? `super-treeview-triangle-btn-up`
                    : `super-treeview-triangle-btn-down`
                }
              />
            </div>

            {this.state.technicalDetailsOpen && (
              <div>
                {props.data.attributes && props.data.attributes.length > 0 && (
                  <div className="list-group px-3">
                    {props.data.observationId && (
                      <div className="detail-container no-gutters">
                        <label
                          htmlFor=""
                          className="detail-container__title col-form-label text-muted"
                        >
                          Observation ID
                        </label>
                        <label
                          htmlFor=""
                          className="col-form-label font-weight-light pl-4 detail-container__value"
                        >
                          {props.data.observationId}
                          {this.state.copying[props.data.observationId] && (
                            <div
                              className="spinner-border copy-spinner"
                              role="status"
                            >
                              <span className="sr-only">Loading...</span>
                            </div>
                          )}
                          {this.state.doneCopying[props.data.observationId] && (
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width="16"
                              height="16"
                              fill="currentColor"
                              className="bi bi-check-lg check-icon"
                              viewBox="0 0 16 16"
                            >
                              <path d="M12.736 3.97a.733.733 0 0 1 1.047 0c.286.289.29.756.01 1.05L7.88 12.01a.733.733 0 0 1-1.065.02L3.217 8.384a.757.757 0 0 1 0-1.06.733.733 0 0 1 1.047 0l3.052 3.093 5.4-6.425z" />
                            </svg>
                          )}
                          {!this.state.copying[props.data.observationId] &&
                            !this.state.doneCopying[
                              props.data.observationId
                            ] && (
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="16"
                                height="16"
                                fill="currentColor"
                                className="bi bi-copy copy-icon"
                                viewBox="0 0 16 16"
                                onClick={() =>
                                  this.copyTextToClipboard(
                                    props.data.observationId
                                  )
                                }
                              >
                                <path
                                  fillRule="evenodd"
                                  d="M4 2a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2zm2-1a1 1 0 0 0-1 1v8a1 1 0 0 0 1 1h8a1 1 0 0 0 1-1V2a1 1 0 0 0-1-1zM2 5a1 1 0 0 0-1 1v8a1 1 0 0 0 1 1h8a1 1 0 0 0 1-1v-1h1v1a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h1v1z"
                                />
                              </svg>
                            )}
                        </label>
                      </div>
                    )}

                    {((props.data.agent && props.data.agent.agentId) ||
                      props.data.attributes.findIndex(
                        attr => attr.key === 'userid' && !isEmpty(attr.value)
                      ) >= 0) && (
                      <div className="detail-container no-gutters">
                        <label
                          htmlFor=""
                          className="detail-container__title col-form-label text-muted"
                        >
                          User ID of Creator
                        </label>
                        <label
                          htmlFor=""
                          className="col-form-label font-weight-light pl-4 detail-container__value"
                        >
                          {props.data.attributes.find(
                            attr => attr.key === 'userid'
                          )
                            ? props.data.attributes.find(
                                attr => attr.key === 'userid'
                              ).value
                            : props.data.agent.agentId}
                          {this.state.copying[
                            props.data.attributes.find(
                              attr => attr.key === 'userid'
                            )
                              ? props.data.attributes.find(
                                  attr => attr.key === 'userid'
                                ).value
                              : props.data.agent.agentId
                          ] && (
                            <div
                              className="spinner-border copy-spinner"
                              role="status"
                            >
                              <span className="sr-only">Loading...</span>
                            </div>
                          )}
                          {this.state.doneCopying[
                            props.data.attributes.find(
                              attr => attr.key === 'userid'
                            )
                              ? props.data.attributes.find(
                                  attr => attr.key === 'userid'
                                ).value
                              : props.data.agent.agentId
                          ] && (
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width="16"
                              height="16"
                              fill="currentColor"
                              className="bi bi-check-lg check-icon"
                              viewBox="0 0 16 16"
                            >
                              <path d="M12.736 3.97a.733.733 0 0 1 1.047 0c.286.289.29.756.01 1.05L7.88 12.01a.733.733 0 0 1-1.065.02L3.217 8.384a.757.757 0 0 1 0-1.06.733.733 0 0 1 1.047 0l3.052 3.093 5.4-6.425z" />
                            </svg>
                          )}
                          {!this.state.copying[
                            props.data.attributes.find(
                              attr => attr.key === 'userid'
                            )
                              ? props.data.attributes.find(
                                  attr => attr.key === 'userid'
                                ).value
                              : props.data.agent.agentId
                          ] &&
                            !this.state.doneCopying[
                              props.data.attributes.find(
                                attr => attr.key === 'userid'
                              )
                                ? props.data.attributes.find(
                                    attr => attr.key === 'userid'
                                  ).value
                                : props.data.agent.agentId
                            ] && (
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="16"
                                height="16"
                                fill="currentColor"
                                className="bi bi-copy copy-icon"
                                viewBox="0 0 16 16"
                                onClick={() =>
                                  this.copyTextToClipboard(
                                    props.data.attributes.find(
                                      attr => attr.key === 'userid'
                                    )
                                      ? props.data.attributes.find(
                                          attr => attr.key === 'userid'
                                        ).value
                                      : props.data.agent.agentId
                                  )
                                }
                              >
                                <path
                                  fillRule="evenodd"
                                  d="M4 2a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2zm2-1a1 1 0 0 0-1 1v8a1 1 0 0 0 1 1h8a1 1 0 0 0 1-1V2a1 1 0 0 0-1-1zM2 5a1 1 0 0 0-1 1v8a1 1 0 0 0 1 1h8a1 1 0 0 0 1-1v-1h1v1a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h1v1z"
                                />
                              </svg>
                            )}
                        </label>
                      </div>
                    )}

                    {props.data.group && props.data.group.groupId && (
                      <div className="detail-container no-gutters">
                        <label
                          htmlFor=""
                          className="detail-container__title col-form-label text-muted"
                        >
                          Group ID
                        </label>
                        <label
                          htmlFor=""
                          className="col-form-label font-weight-light pl-4 detail-container__value"
                        >
                          {props.data.group.groupId}
                          {this.state.copying[props.data.group.groupId] && (
                            <div
                              className="spinner-border copy-spinner"
                              role="status"
                            >
                              <span className="sr-only">Loading...</span>
                            </div>
                          )}
                          {this.state.doneCopying[props.data.group.groupId] && (
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width="16"
                              height="16"
                              fill="currentColor"
                              className="bi bi-check-lg check-icon"
                              viewBox="0 0 16 16"
                            >
                              <path d="M12.736 3.97a.733.733 0 0 1 1.047 0c.286.289.29.756.01 1.05L7.88 12.01a.733.733 0 0 1-1.065.02L3.217 8.384a.757.757 0 0 1 0-1.06.733.733 0 0 1 1.047 0l3.052 3.093 5.4-6.425z" />
                            </svg>
                          )}
                          {!this.state.copying[props.data.group.groupId] &&
                            !this.state.doneCopying[
                              props.data.group.groupId
                            ] && (
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="16"
                                height="16"
                                fill="currentColor"
                                className="bi bi-copy copy-icon"
                                viewBox="0 0 16 16"
                                onClick={() =>
                                  this.copyTextToClipboard(
                                    props.data.group.groupId
                                  )
                                }
                              >
                                <path
                                  fillRule="evenodd"
                                  d="M4 2a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2zm2-1a1 1 0 0 0-1 1v8a1 1 0 0 0 1 1h8a1 1 0 0 0 1-1V2a1 1 0 0 0-1-1zM2 5a1 1 0 0 0-1 1v8a1 1 0 0 0 1 1h8a1 1 0 0 0 1-1v-1h1v1a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h1v1z"
                                />
                              </svg>
                            )}
                        </label>
                      </div>
                    )}

                    {props.data.attributes.findIndex(
                      attr => attr.key === 'editorId' && !isEmpty(attr.value)
                    ) >= 0 && (
                      <div className="detail-container no-gutters">
                        <label
                          htmlFor=""
                          className="detail-container__title col-form-label text-muted"
                        >
                          User ID of Editor
                        </label>
                        <label
                          htmlFor=""
                          className="col-form-label font-weight-light pl-4 detail-container__value"
                        >
                          {
                            props.data.attributes.find(
                              attr => attr.key === 'editorId'
                            ).value
                          }
                          {this.state.copying[
                            props.data.attributes.find(
                              attr => attr.key === 'editorId'
                            ).value
                          ] && (
                            <div
                              className="spinner-border copy-spinner"
                              role="status"
                            >
                              <span className="sr-only">Loading...</span>
                            </div>
                          )}
                          {this.state.doneCopying[
                            props.data.attributes.find(
                              attr => attr.key === 'editorId'
                            ).value
                          ] && (
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width="16"
                              height="16"
                              fill="currentColor"
                              className="bi bi-check-lg check-icon"
                              viewBox="0 0 16 16"
                            >
                              <path d="M12.736 3.97a.733.733 0 0 1 1.047 0c.286.289.29.756.01 1.05L7.88 12.01a.733.733 0 0 1-1.065.02L3.217 8.384a.757.757 0 0 1 0-1.06.733.733 0 0 1 1.047 0l3.052 3.093 5.4-6.425z" />
                            </svg>
                          )}
                          {!this.state.copying[
                            props.data.attributes.find(
                              attr => attr.key === 'editorId'
                            ).value
                          ] &&
                            !this.state.doneCopying[
                              props.data.attributes.find(
                                attr => attr.key === 'editorId'
                              ).value
                            ] && (
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="16"
                                height="16"
                                fill="currentColor"
                                className="bi bi-copy copy-icon"
                                viewBox="0 0 16 16"
                                onClick={() =>
                                  this.copyTextToClipboard(
                                    props.data.attributes.find(
                                      attr => attr.key === 'editorId'
                                    ).value
                                  )
                                }
                              >
                                <path
                                  fillRule="evenodd"
                                  d="M4 2a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2zm2-1a1 1 0 0 0-1 1v8a1 1 0 0 0 1 1h8a1 1 0 0 0 1-1V2a1 1 0 0 0-1-1zM2 5a1 1 0 0 0-1 1v8a1 1 0 0 0 1 1h8a1 1 0 0 0 1-1v-1h1v1a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h1v1z"
                                />
                              </svg>
                            )}
                        </label>
                      </div>
                    )}

                    {props.data.attributes.findIndex(
                      attr => attr.key === 'EUNIS' && !isEmpty(attr.value)
                    ) >= 0 && (
                      <div className="detail-container no-gutters">
                        <label
                          htmlFor=""
                          className="detail-container__title col-form-label text-muted"
                        >
                          EUNIS
                        </label>
                        <label
                          htmlFor=""
                          className="col-form-label font-weight-light pl-4 detail-container__value"
                        >
                          {
                            props.data.attributes.find(
                              attr => attr.key === 'EUNIS'
                            ).value
                          }
                          {this.state.copying[
                            props.data.attributes.find(
                              attr => attr.key === 'EUNIS'
                            ).value
                          ] && (
                            <div
                              className="spinner-border copy-spinner"
                              role="status"
                            >
                              <span className="sr-only">Loading...</span>
                            </div>
                          )}
                          {this.state.doneCopying[
                            props.data.attributes.find(
                              attr => attr.key === 'EUNIS'
                            ).value
                          ] && (
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width="16"
                              height="16"
                              fill="currentColor"
                              className="bi bi-check-lg check-icon"
                              viewBox="0 0 16 16"
                            >
                              <path d="M12.736 3.97a.733.733 0 0 1 1.047 0c.286.289.29.756.01 1.05L7.88 12.01a.733.733 0 0 1-1.065.02L3.217 8.384a.757.757 0 0 1 0-1.06.733.733 0 0 1 1.047 0l3.052 3.093 5.4-6.425z" />
                            </svg>
                          )}
                          {!this.state.copying[
                            props.data.attributes.find(
                              attr => attr.key === 'EUNIS'
                            ).value
                          ] &&
                            !this.state.doneCopying[
                              props.data.attributes.find(
                                attr => attr.key === 'EUNIS'
                              ).value
                            ] && (
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="16"
                                height="16"
                                fill="currentColor"
                                className="bi bi-copy copy-icon"
                                viewBox="0 0 16 16"
                                onClick={() =>
                                  this.copyTextToClipboard(
                                    props.data.attributes.find(
                                      attr => attr.key === 'EUNIS'
                                    ).value
                                  )
                                }
                              >
                                <path
                                  fillRule="evenodd"
                                  d="M4 2a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2zm2-1a1 1 0 0 0-1 1v8a1 1 0 0 0 1 1h8a1 1 0 0 0 1-1V2a1 1 0 0 0-1-1zM2 5a1 1 0 0 0-1 1v8a1 1 0 0 0 1 1h8a1 1 0 0 0 1-1v-1h1v1a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h1v1z"
                                />
                              </svg>
                            )}
                        </label>
                      </div>
                    )}

                    {props.data.attributes.findIndex(
                      attr => attr.key === 'X3035' && !isEmpty(attr.value)
                    ) >= 0 && (
                      <div className="detail-container no-gutters">
                        <label
                          htmlFor=""
                          className="detail-container__title col-form-label text-muted"
                        >
                          X3035
                        </label>
                        <label
                          htmlFor=""
                          className="col-form-label font-weight-light pl-4 detail-container__value"
                        >
                          {
                            props.data.attributes.find(
                              attr => attr.key === 'X3035'
                            ).value
                          }
                          {this.state.copying[
                            props.data.attributes.find(
                              attr => attr.key === 'X3035'
                            ).value
                          ] && (
                            <div
                              className="spinner-border copy-spinner"
                              role="status"
                            >
                              <span className="sr-only">Loading...</span>
                            </div>
                          )}
                          {this.state.doneCopying[
                            props.data.attributes.find(
                              attr => attr.key === 'X3035'
                            ).value
                          ] && (
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width="16"
                              height="16"
                              fill="currentColor"
                              className="bi bi-check-lg check-icon"
                              viewBox="0 0 16 16"
                            >
                              <path d="M12.736 3.97a.733.733 0 0 1 1.047 0c.286.289.29.756.01 1.05L7.88 12.01a.733.733 0 0 1-1.065.02L3.217 8.384a.757.757 0 0 1 0-1.06.733.733 0 0 1 1.047 0l3.052 3.093 5.4-6.425z" />
                            </svg>
                          )}
                          {!this.state.copying[
                            props.data.attributes.find(
                              attr => attr.key === 'X3035'
                            ).value
                          ] &&
                            !this.state.doneCopying[
                              props.data.attributes.find(
                                attr => attr.key === 'X3035'
                              ).value
                            ] && (
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="16"
                                height="16"
                                fill="currentColor"
                                className="bi bi-copy copy-icon"
                                viewBox="0 0 16 16"
                                onClick={() =>
                                  this.copyTextToClipboard(
                                    props.data.attributes.find(
                                      attr => attr.key === 'X3035'
                                    ).value
                                  )
                                }
                              >
                                <path
                                  fillRule="evenodd"
                                  d="M4 2a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2zm2-1a1 1 0 0 0-1 1v8a1 1 0 0 0 1 1h8a1 1 0 0 0 1-1V2a1 1 0 0 0-1-1zM2 5a1 1 0 0 0-1 1v8a1 1 0 0 0 1 1h8a1 1 0 0 0 1-1v-1h1v1a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h1v1z"
                                />
                              </svg>
                            )}
                        </label>
                      </div>
                    )}

                    {props.data.attributes.findIndex(
                      attr => attr.key === 'Y3035' && !isEmpty(attr.value)
                    ) >= 0 && (
                      <div className="detail-container no-gutters">
                        <label
                          htmlFor=""
                          className="detail-container__title col-form-label text-muted"
                        >
                          Y3035
                        </label>
                        <label
                          htmlFor=""
                          className="col-form-label font-weight-light pl-4 detail-container__value"
                        >
                          {
                            props.data.attributes.find(
                              attr => attr.key === 'Y3035'
                            ).value
                          }
                          {this.state.copying[
                            props.data.attributes.find(
                              attr => attr.key === 'Y3035'
                            ).value
                          ] && (
                            <div
                              className="spinner-border copy-spinner"
                              role="status"
                            >
                              <span className="sr-only">Loading...</span>
                            </div>
                          )}
                          {this.state.doneCopying[
                            props.data.attributes.find(
                              attr => attr.key === 'Y3035'
                            ).value
                          ] && (
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width="16"
                              height="16"
                              fill="currentColor"
                              className="bi bi-check-lg check-icon"
                              viewBox="0 0 16 16"
                            >
                              <path d="M12.736 3.97a.733.733 0 0 1 1.047 0c.286.289.29.756.01 1.05L7.88 12.01a.733.733 0 0 1-1.065.02L3.217 8.384a.757.757 0 0 1 0-1.06.733.733 0 0 1 1.047 0l3.052 3.093 5.4-6.425z" />
                            </svg>
                          )}
                          {!this.state.copying[
                            props.data.attributes.find(
                              attr => attr.key === 'Y3035'
                            ).value
                          ] &&
                            !this.state.doneCopying[
                              props.data.attributes.find(
                                attr => attr.key === 'Y3035'
                              ).value
                            ] && (
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="16"
                                height="16"
                                fill="currentColor"
                                className="bi bi-copy copy-icon"
                                viewBox="0 0 16 16"
                                onClick={() =>
                                  this.copyTextToClipboard(
                                    props.data.attributes.find(
                                      attr => attr.key === 'Y3035'
                                    ).value
                                  )
                                }
                              >
                                <path
                                  fillRule="evenodd"
                                  d="M4 2a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2zm2-1a1 1 0 0 0-1 1v8a1 1 0 0 0 1 1h8a1 1 0 0 0 1-1V2a1 1 0 0 0-1-1zM2 5a1 1 0 0 0-1 1v8a1 1 0 0 0 1 1h8a1 1 0 0 0 1-1v-1h1v1a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h1v1z"
                                />
                              </svg>
                            )}
                        </label>
                      </div>
                    )}

                    {trackIdValue && (
                      <div className="detail-container no-gutters">
                        <label
                          htmlFor=""
                          className="detail-container__title col-form-label text-muted"
                        >
                          Track ID
                        </label>
                        <label
                          htmlFor=""
                          className="col-form-label font-weight-light pl-4 detail-container__value"
                        >
                          {trackIdValue}
                          {this.state.copying[trackIdValue] && (
                            <div
                              className="spinner-border copy-spinner"
                              role="status"
                            >
                              <span className="sr-only">Loading...</span>
                            </div>
                          )}
                          {this.state.doneCopying[trackIdValue] && (
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width="16"
                              height="16"
                              fill="currentColor"
                              className="bi bi-check-lg check-icon"
                              viewBox="0 0 16 16"
                            >
                              <path d="M12.736 3.97a.733.733 0 0 1 1.047 0c.286.289.29.756.01 1.05L7.88 12.01a.733.733 0 0 1-1.065.02L3.217 8.384a.757.757 0 0 1 0-1.06.733.733 0 0 1 1.047 0l3.052 3.093 5.4-6.425z" />
                            </svg>
                          )}
                          {!this.state.copying[trackIdValue] &&
                            !this.state.doneCopying[trackIdValue] && (
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="16"
                                height="16"
                                fill="currentColor"
                                className="bi bi-copy copy-icon"
                                viewBox="0 0 16 16"
                                onClick={() =>
                                  this.copyTextToClipboard(trackIdValue)
                                }
                              >
                                <path
                                  fillRule="evenodd"
                                  d="M4 2a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2zm2-1a1 1 0 0 0-1 1v8a1 1 0 0 0 1 1h8a1 1 0 0 0 1-1V2a1 1 0 0 0-1-1zM2 5a1 1 0 0 0-1 1v8a1 1 0 0 0 1 1h8a1 1 0 0 0 1-1v-1h1v1a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h1v1z"
                                />
                              </svg>
                            )}
                        </label>
                      </div>
                    )}

                    {props.data.attributes.findIndex(
                      attr => attr.key === 'appVersion' && !isEmpty(attr.value)
                    ) >= 0 && (
                      <div className="detail-container no-gutters">
                        <label
                          htmlFor=""
                          className="detail-container__title col-form-label text-muted"
                        >
                          Cluey version
                        </label>
                        <label
                          htmlFor=""
                          className="col-form-label font-weight-light pl-4 detail-container__value"
                        >
                          {
                            props.data.attributes.find(
                              attr => attr.key === 'appVersion'
                            ).value
                          }
                          {this.state.copying[
                            props.data.attributes.find(
                              attr => attr.key === 'appVersion'
                            ).value
                          ] && (
                            <div
                              className="spinner-border copy-spinner"
                              role="status"
                            >
                              <span className="sr-only">Loading...</span>
                            </div>
                          )}
                          {this.state.doneCopying[
                            props.data.attributes.find(
                              attr => attr.key === 'appVersion'
                            ).value
                          ] && (
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width="16"
                              height="16"
                              fill="currentColor"
                              className="bi bi-check-lg check-icon"
                              viewBox="0 0 16 16"
                            >
                              <path d="M12.736 3.97a.733.733 0 0 1 1.047 0c.286.289.29.756.01 1.05L7.88 12.01a.733.733 0 0 1-1.065.02L3.217 8.384a.757.757 0 0 1 0-1.06.733.733 0 0 1 1.047 0l3.052 3.093 5.4-6.425z" />
                            </svg>
                          )}
                          {!this.state.copying[
                            props.data.attributes.find(
                              attr => attr.key === 'appVersion'
                            ).value
                          ] &&
                            !this.state.doneCopying[
                              props.data.attributes.find(
                                attr => attr.key === 'appVersion'
                              ).value
                            ] && (
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="16"
                                height="16"
                                fill="currentColor"
                                className="bi bi-copy copy-icon"
                                viewBox="0 0 16 16"
                                onClick={() =>
                                  this.copyTextToClipboard(
                                    props.data.attributes.find(
                                      attr => attr.key === 'appVersion'
                                    ).value
                                  )
                                }
                              >
                                <path
                                  fillRule="evenodd"
                                  d="M4 2a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2zm2-1a1 1 0 0 0-1 1v8a1 1 0 0 0 1 1h8a1 1 0 0 0 1-1V2a1 1 0 0 0-1-1zM2 5a1 1 0 0 0-1 1v8a1 1 0 0 0 1 1h8a1 1 0 0 0 1-1v-1h1v1a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h1v1z"
                                />
                              </svg>
                            )}
                        </label>
                      </div>
                    )}

                    <div className="detail-container no-gutters">
                      <label
                        htmlFor=""
                        className="detail-container__title col-form-label text-muted"
                      >
                        Raw data
                      </label>
                      <div className="col-form-label font-weight-light">
                        <div className="observation-raw-data-actions">
                          <button
                            type="button"
                            className="btn btn-outline-secondary btn-sm"
                            onClick={() =>
                              this.setState({
                                rawDataOpen: !this.state.rawDataOpen
                              })
                            }
                          >
                            {this.state.rawDataOpen ? 'Hide' : 'Show'}
                          </button>
                          {this.state.copying[rawDataCopyKey] && (
                            <div
                              className="spinner-border copy-spinner"
                              role="status"
                            >
                              <span className="sr-only">Loading...</span>
                            </div>
                          )}
                          {this.state.doneCopying[rawDataCopyKey] && (
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width="16"
                              height="16"
                              fill="currentColor"
                              className="bi bi-check-lg check-icon"
                              viewBox="0 0 16 16"
                            >
                              <path d="M12.736 3.97a.733.733 0 0 1 1.047 0c.286.289.29.756.01 1.05L7.88 12.01a.733.733 0 0 1-1.065.02L3.217 8.384a.757.757 0 0 1 0-1.06.733.733 0 0 1 1.047 0l3.052 3.093 5.4-6.425z" />
                            </svg>
                          )}
                          {!this.state.copying[rawDataCopyKey] &&
                            !this.state.doneCopying[rawDataCopyKey] && (
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="16"
                                height="16"
                                fill="currentColor"
                                className="bi bi-copy copy-icon"
                                viewBox="0 0 16 16"
                                title="Copy raw data to clipboard"
                                onClick={() =>
                                  this.copyTextToClipboard(
                                    rawDataText,
                                    rawDataCopyKey
                                  )
                                }
                              >
                                <path
                                  fillRule="evenodd"
                                  d="M4 2a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2zm2-1a1 1 0 0 0-1 1v8a1 1 0 0 0 1 1h8a1 1 0 0 0 1-1V2a1 1 0 0 0-1-1zM2 5a1 1 0 0 0-1 1v8a1 1 0 0 0 1 1h8a1 1 0 0 0 1-1v-1h1v1a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h1v1z"
                                />
                              </svg>
                            )}
                        </div>
                      </div>
                    </div>
                    {this.state.rawDataOpen && (
                      <div className="detail-container no-gutters">
                        <pre className="raw-data-modal-pre">{rawDataText}</pre>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        </Modal.Body>
      </Modal>
    );
  }
}

export default ObservationModal;
